import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";

export const Route = createFileRoute("/_authed/reports/")({
  component: ReportsQueue,
});

type Group = components["schemas"]["ReportGroup"];
type Action = components["schemas"]["ReportResolve"]["action"];

const REASON: Record<string, string> = {
  sexual: "Sexual content",
  illegal: "Illegal",
  cruelty: "Animal cruelty",
  harassment: "Harassment",
  spam: "Spam",
  other: "Something else",
};

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * The moderation queue: every reported item, grouped, with the content in
 * front of you and one decision per group. Community surfaces (packs,
 * places, shared memories, trails) auto-hide at two reporters, so "hidden"
 * here usually means the community already did the first step.
 */
function ReportsQueue() {
  const [status, setStatus] = useState<"open" | "resolved">("open");
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "reports", status],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/reports", {
        params: { query: { status, limit: 100 } },
      });
      if (error) throw new Error("failed to load reports");
      return data!;
    },
    refetchInterval: status === "open" ? 60_000 : false,
  });
  const resolve = useMutation({
    mutationFn: async (v: { g: Group; action: Action }) => {
      const { error } = await api.POST("/api/v1/admin/reports/{content_type}/{content_id}/resolve", {
        params: { path: { content_type: v.g.content_type, content_id: v.g.content_id } },
        body: { action: v.action },
      });
      if (error) throw new Error("failed to resolve");
    },
    onSuccess: () => {
      setRemoveTarget(null);
      void qc.invalidateQueries({ queryKey: ["admin", "reports"] });
    },
  });

  const groups = query.data ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">Reports</h1>
          <p className="text-sm text-zinc-500">
            What members flagged, newest first. Dismiss when it's fine, remove when it isn't.
          </p>
        </div>
        <div className="flex gap-1 rounded-md border border-zinc-200 p-0.5 text-sm">
          {(["open", "resolved"] as const).map((s) => (
            <button
              key={s}
              type="button"
              disabled={resolve.isPending}
              onClick={() => { setStatus(s); setRemoveTarget(null); resolve.reset(); }}
              className={`rounded px-3 py-1 ${status === s ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            >
              {s === "open" ? "Open" : "Resolved"}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : query.isError ? (
        <Card><CardBody>
          <p role="alert" className="text-sm text-red-700">Couldn’t load reports. Your session may have expired.</p>
          <button type="button" className="mt-3 rounded border px-3 py-2 text-sm" disabled={query.isFetching} onClick={() => void query.refetch()}>Retry loading reports</button>
        </CardBody></Card>
      ) : groups.length === 0 ? (
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Flag className="h-4 w-4" /> {status === "open" ? "Nothing waiting." : "Nothing resolved yet."}
            </div>
          </CardBody>
        </Card>
      ) : (
        groups.map((g) => {
          const p = g.preview;
          const key = `${g.content_type}:${g.content_id}`;
          const affected = resolve.variables?.g.content_type === g.content_type && resolve.variables?.g.content_id === g.content_id;
          const busy = resolve.isPending && affected;
          return (
            <Card key={key}>
              <CardBody>
                <div className="flex gap-4">
                  {p.photo_url ? (
                    <a href={p.photo_url} target="_blank" rel="noreferrer" className="shrink-0">
                      <img
                        src={p.photo_url}
                        alt=""
                        className="h-28 w-28 rounded-md border border-zinc-200 object-cover"
                      />
                    </a>
                  ) : (
                    <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-md border border-dashed border-zinc-200 text-xs text-zinc-400">
                      no image
                    </div>
                  )}
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="rounded bg-zinc-100 px-2 py-0.5 font-medium">{p.label}</span>
                      {p.deleted ? (
                        <span className="rounded bg-red-50 px-2 py-0.5 text-red-700">removed</span>
                      ) : p.hidden ? (
                        <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-700">hidden</span>
                      ) : null}
                      <span className="text-zinc-500">
                        by {p.author_name ?? "unknown"} · {g.count} report{g.count === 1 ? "" : "s"} ·{" "}
                        {when(g.last_at)}
                      </span>
                    </div>
                    {p.text ? (
                      <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">{p.text}</p>
                    ) : (
                      <p className="text-sm italic text-zinc-400">(no text)</p>
                    )}
                    <div className="text-sm text-zinc-600">
                      <span className="font-medium">Reasons:</span>{" "}
                      {g.reasons.map((r) => REASON[r] ?? r).join(", ") || "—"}
                      {g.notes.length ? (
                        <ul className="mt-1 list-disc pl-5 text-zinc-500">
                          {g.notes.map((n, i) => (
                            <li key={i}>“{n}”</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <div className="text-xs text-zinc-500">
                      Reported by {g.reporters.map((r) => r.display_name ?? r.id.slice(0, 8)).join(", ")}
                      {g.resolution ? ` · resolved: ${g.resolution}${g.resolved_at ? ` (${when(g.resolved_at)})` : ""}` : ""}
                    </div>
                    {status === "open" ? (
                      <div className="space-y-2 pt-1">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={resolve.isPending}
                            onClick={() => resolve.mutate({ g, action: "dismiss" })}
                          >
                            Dismiss — it's fine
                          </Button>
                          {p.can_hide && !p.deleted ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={resolve.isPending}
                              onClick={() => resolve.mutate({ g, action: p.hidden ? "unhide" : "hide" })}
                            >
                              {p.hidden ? "Unhide" : "Hide"}
                            </Button>
                          ) : null}
                          {!p.deleted ? (
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={resolve.isPending}
                              onClick={() => { setRemoveTarget(key); resolve.reset(); }}
                            >
                              Remove
                            </Button>
                          ) : null}
                        </div>
                        {removeTarget === key && !p.deleted ? (
                          <div role="group" aria-label="Confirm content removal" className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3">
                            <p className="text-sm font-medium text-zinc-900">Remove this content for everyone?</p>
                            <p className="text-sm text-zinc-600">This cannot be undone from here.</p>
                            <div className="flex gap-2">
                              <Button size="sm" variant="secondary" disabled={resolve.isPending} onClick={() => setRemoveTarget(null)}>Cancel</Button>
                              <Button size="sm" variant="danger" disabled={resolve.isPending} onClick={() => resolve.mutate({ g, action: "remove" })}>
                                {busy ? "Removing…" : "Remove for everyone"}
                              </Button>
                            </div>
                          </div>
                        ) : null}
                        {resolve.isError && affected ? <p role="alert" className="text-sm text-red-700">Couldn’t resolve this report. Please try again.</p> : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </CardBody>
            </Card>
          );
        })
      )}
    </div>
  );
}
